import { CMSUserPermission, ModeType, UserRole } from "helpers";

import { Navigate } from "react-router-dom";
import { lazy } from "react";

const Dashboard = lazy(() => import("pages/dashboard/Dashboard"));
const RemittanceTab = lazy(() => import("pages/remittance-tab/RemittanceTab"));
const Performance = lazy(() => import("pages/performance/Performance"));
const PGAFormComponent = lazy(() => import("pages/pga-form/PGAFormComponent"));
const DocumentHub = lazy(() => import("pages/document-hub/DocumentHub"));
const MyProfile = lazy(() => import("pages/my-profile/MyProfile"));
const AlertList = lazy(() => import("pages/alert-list/AlertList"));
const PreferenceSetting = lazy(() => import("pages/preference-setting/PreferenceSetting"));
const ReportWeight = lazy(() => import("pages/report-weight/ReportWeight"));
const ReportCalendarOfSupply = lazy(
  () => import("pages/report-calendar-of-supply/ReportCalendarOfSupply")
);
const ReportDeadWeightGain = lazy(
  () => import("pages/report-dead-weight-gain/ReportDeadWeightGain")
);
const ReportPostMortem = lazy(() => import("pages/report-post-mortem/ReportPostMortem"));
const ReportSummary = lazy(() => import("pages/report-summary/ReportSummary"));
const ReportGrade = lazy(() => import("pages/report-grade/ReportGrade"));
const SupportForm = lazy(() => import("pages/support-form/SupportForm"));
const BlankPage = lazy(() => import("pages/blank/BlankPage"));
const FrequentlyAskedQuestions = lazy(
  () => import("pages/frequently-asked-questions/FrequentlyAskedQuestions")
);
const CookiesPolicy = lazy(() => import("pages/cookies-policy/CookiesPolicy"));
const FarmGroup = lazy(() => import("pages/farm-group/FarmGroup"));
const BenchmarkReport = lazy(() => import("pages/farm-group/BenchmarkReport"));
const MyDocuments = lazy(() => import("pages/my-documents/MyDocuments"));

const routes: Array<{
  path: string;
  name?: string;
  component: any;
  title?: string;
  exact?: boolean;
  useAuthLayout?: boolean;
  isSettingMenu?: boolean;
  isSubModule?: boolean;
  isDisplaySwitchMode?: boolean;
  isDisplayModeHeader?: boolean;
  keepQueryParam?: boolean;
  breadcrumbs?: any[];
  modeTypes?: ModeType[];
  temporaryUsers?: UserRole[];
  requiredPermission?: string[];
  exceptedPermission?: string;
}> = [
  {
    path: "/dashboard",
    name: "dashboard",
    component: Dashboard,
    title: "Dashboard",
    useAuthLayout: true,
    isDisplaySwitchMode: true,
  },
  {
    path: "/remittance/detail",
    name: "remittance-detail",
    component: RemittanceTab,
    title: "RemittanceDetail",
    useAuthLayout: true,
    isSubModule: true,
    isDisplaySwitchMode: true,
    breadcrumbs: [
      { title: "Back to Remittance list", path: "/remittance" },
      { title: "Lot Details" },
    ],
  },
  {
    path: "/remittance/custom-vat-summary",
    name: "Custom VAT Summary",
    component: RemittanceTab,
    title: "Custom VAT Summary",
    useAuthLayout: true,
    isSubModule: true,
    isDisplaySwitchMode: true,
    breadcrumbs: [{ title: "Custom VAT Summary" }],
  },
  {
    path: "/remittance",
    name: "remittance",
    component: RemittanceTab,
    title: "Remittance",
    useAuthLayout: true,
    isDisplaySwitchMode: true,
    breadcrumbs: [{ title: "Remittances" }],
  },
  {
    path: "/pga-form",
    name: "pga-form",
    component: PGAFormComponent,
    title: "PGAForm",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/document-hub",
    name: "document-hub",
    component: DocumentHub,
    title: "DocumentHub",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/frequently-asked-questions",
    name: "Frequently Asked Questions",
    component: FrequentlyAskedQuestions,
    title: "FrequentlyAskedQuestions",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/cookies-policy",
    name: "Cookies Policy",
    component: CookiesPolicy,
    title: "CookiesPolicy",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/my-profile",
    name: "my-profile",
    component: MyProfile,
    title: "MyProfile",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/preference-setting",
    name: "preference-setting",
    component: PreferenceSetting,
    title: "PreferenceSetting",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/alert-list",
    name: "alert-list",
    component: AlertList,
    title: "AlertList",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/performance/report-weight",
    name: "report-weight",
    component: ReportWeight,
    title: "ReportWeight",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    keepQueryParam: true,
    isDisplayModeHeader: true,
    breadcrumbs: [
      { title: "Back to Performance reports", path: "/performance" },
      { title: "View Report" },
    ],
  },
  {
    path: "/performance/report-grade",
    name: "report-grade",
    component: ReportGrade,
    title: "ReportGrade",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    keepQueryParam: true,
    isDisplayModeHeader: true,
    breadcrumbs: [
      { title: "Back to Performance reports", path: "/performance" },
      { title: "View Report" },
    ],
  },
  {
    path: "/performance/report-supply",
    name: "report-supply",
    component: ReportCalendarOfSupply,
    title: "ReportCalendarOfSupply",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    keepQueryParam: true,
    isDisplayModeHeader: true,
    breadcrumbs: [
      { title: "Back to Performance reports", path: "/performance" },
      { title: "View Report" },
    ],
  },
  {
    path: "/performance/report-gain",
    name: "report-gain",
    component: ReportDeadWeightGain,
    title: "ReportDeadWeighGain",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    keepQueryParam: true,
    isDisplayModeHeader: true,
    breadcrumbs: [
      { title: "Back to Performance reports", path: "/performance" },
      { title: "View Report" },
    ],
  },
  {
    path: "/performance/report-mortem",
    name: "report-mortem",
    component: ReportPostMortem,
    title: "ReportPostMortem",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    keepQueryParam: true,
    isDisplayModeHeader: true,
    breadcrumbs: [
      { title: "Back to Performance reports", path: "/performance" },
      { title: "View Report" },
    ],
  },
  {
    path: "/performance/report-summary",
    name: "report-summary",
    component: ReportSummary,
    title: "ReportSummary",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    keepQueryParam: true,
    isDisplayModeHeader: true,
    breadcrumbs: [
      { title: "Back to Performance reports", path: "/performance" },
      { title: "View Report" },
    ],
  },
  {
    path: "/performance",
    name: "performance",
    component: Performance,
    title: "Performance",
    useAuthLayout: true,
    isDisplaySwitchMode: true,
    breadcrumbs: [{ title: "Performance" }],
  },
  {
    path: "/support-form",
    name: "support-form",
    component: SupportForm,
    title: "SupportForm",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/blank-page",
    name: "blank-page",
    component: BlankPage,
    title: "BlankPage",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/",
    exact: true,
    component: () => <Navigate to="/c/dashboard" />,
    useAuthLayout: true,
    isDisplaySwitchMode: false,
  },
  {
    path: "/benchmarking-group/benchmark-report",
    name: "benchmark-report",
    component: BenchmarkReport,
    title: "Benchmark Report",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    isDisplayModeHeader: true,
    modeTypes: [ModeType.Cattle],
    breadcrumbs: [
      { title: "Back to Benchmarking Groups list", path: "/benchmarking-group" },
      { title: "Benchmark Report" },
    ],
    // temporaryUsers: [UserRole.Admin, UserRole.Farmer, UserRole.AgricultureManager],
    exceptedPermission: CMSUserPermission.AccessPortal_ExceptMyDocuments_BenchmarkingGroups,
    requiredPermission: [CMSUserPermission.AccessPortal_ViewAll]
  },
  {
    path: "/benchmarking-group",
    name: "benchmarking-group",
    component: FarmGroup,
    title: "Benchmarking Groups",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    isDisplayModeHeader: true,
    modeTypes: [ModeType.Cattle],
    breadcrumbs: [{ title: "Benchmarking Groups" }],
    // temporaryUsers: [UserRole.Admin, UserRole.Farmer, UserRole.AgricultureManager],
    exceptedPermission: CMSUserPermission.AccessPortal_ExceptMyDocuments_BenchmarkingGroups,
    requiredPermission: [CMSUserPermission.AccessPortal_ViewAll]
  },
  {
    path: "/my-documents",
    name: "my-documents",
    component: MyDocuments,
    title: "My Documents",
    useAuthLayout: true,
    isDisplaySwitchMode: false,
    isDisplayModeHeader: true,
    breadcrumbs: [{ title: "My Documents" }],
    // temporaryUsers: [UserRole.Admin, UserRole.Farmer, UserRole.AgricultureManager],
    exceptedPermission: CMSUserPermission.AccessPortal_ExceptMyDocuments_BenchmarkingGroups,
    requiredPermission: [CMSUserPermission.AccessPortal_ViewAll]
  },
];
export { routes };
